import {
    formatBackendDatePtBr,
    formatIsoDate,
    normalizeDayStart,
    normalizeMonthStart,
    parseBackendDate
} from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';

export const CRM_CUPONS_API = 'crm/cupons';

export const ALL_EVENTS_OPTION = 'all';

export const fetchCupons = async () => {
    const response = await fetchFlask(CRM_CUPONS_API);

    if (response?.data === undefined) {
        throw new Error(response?.message || `Resposta inválida de ${CRM_CUPONS_API}.`);
    }

    return response.data;
};

// O backend envia inicio (assinatura do cliente), data (uso do cupom) e evento.
export const buildCupons = (rows) => rows.map((row, index) => {
    const signatureDate = parseBackendDate(row.inicio);

    return {
        id: index,
        evento: row.evento,
        couponDate: parseBackendDate(row.data),
        signatureMonth: signatureDate ? normalizeMonthStart(signatureDate) : null
    };
});

export const buildEventOptions = (cupons) => Array.from(new Set(cupons.map((cupom) => cupom.evento)))
    .sort((leftEvent, rightEvent) => String(leftEvent).localeCompare(String(rightEvent), 'pt-BR'))
    .map((evento) => ({ value: evento, label: evento }));

// O range de assinatura abre cobrindo todos os meses recebidos, para nada ficar escondido.
export const buildSignatureMonthBounds = (cupons) => {
    const months = cupons
        .map((cupom) => cupom.signatureMonth)
        .filter(Boolean)
        .map((month) => month.getTime());

    if (months.length === 0) {
        const currentMonth = normalizeMonthStart(new Date());
        return { startDate: currentMonth, endDate: currentMonth };
    }

    return {
        startDate: new Date(Math.min(...months)),
        endDate: new Date(Math.max(...months))
    };
};

export const filterCupons = ({ cupons, selectedEvents, signatureRange }) => {
    const hasEventFilter = selectedEvents.length > 0 && !selectedEvents.includes(ALL_EVENTS_OPTION);
    const startMonth = normalizeMonthStart(signatureRange.startDate).getTime();
    const endMonth = normalizeMonthStart(signatureRange.endDate).getTime();

    return cupons.filter((cupom) => {
        if (hasEventFilter && !selectedEvents.includes(cupom.evento)) return false;
        // Cliente sem data de início não cabe em nenhum mês do recorte.
        if (!cupom.signatureMonth) return false;

        const signatureTime = cupom.signatureMonth.getTime();

        return signatureTime >= startMonth && signatureTime <= endMonth;
    });
};

export const buildDailyCupons = (cupons) => {
    const couponDays = cupons
        .map((cupom) => cupom.couponDate)
        .filter(Boolean)
        .map((couponDate) => normalizeDayStart(couponDate));

    if (couponDays.length === 0) return [];

    const dayTimes = couponDays.map((couponDay) => couponDay.getTime());
    const lastDayTime = Math.max(...dayTimes);
    const countByDay = new Map();

    for (
        const currentDay = new Date(Math.min(...dayTimes));
        currentDay.getTime() <= lastDayTime;
        currentDay.setDate(currentDay.getDate() + 1)
    ) {
        countByDay.set(formatIsoDate(currentDay), 0);
    }

    couponDays.forEach((couponDay) => {
        const dayKey = formatIsoDate(couponDay);
        countByDay.set(dayKey, countByDay.get(dayKey) + 1);
    });

    return Array.from(countByDay.entries()).map(([day, cuponsCount]) => ({
        day,
        label: formatBackendDatePtBr(`${day}T00:00:00`),
        cupons: cuponsCount
    }));
};

export const buildCuponsSummary = ({ cupons, dailyCupons }) => {
    const peak = dailyCupons.reduce(
        (bestDay, currentDay) => currentDay.cupons > bestDay.cupons ? currentDay : bestDay,
        { cupons: 0, label: '' }
    );
    const daysWithCupons = dailyCupons.filter((day) => day.cupons > 0).length;

    return {
        total: cupons.length,
        events: new Set(cupons.map((cupom) => cupom.evento)).size,
        peak,
        daysWithCupons,
        dailyAverage: daysWithCupons > 0 ? cupons.length / daysWithCupons : 0
    };
};
